import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { toast } from 'react-hot-toast';
import { formatEnumLabel } from '../../../constants/enums';
import IntegrationDialog, { emptyIntegrationRow } from '../../../shared/IntegrationDialog';
import { apiErrorMessage } from '../../../utils/responseHelpers';
import {
  persistWizardNestedCollection,
  persistWizardSoftwareFields,
} from '../../../utils/persistWizardNestedCollection';

export default function WizardStepIntegration({
  values,
  onSetField,
  softwareId = null,
  softwareName = '',
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const primary = theme.palette.primary.main;

  const entries = values?.entries ?? [];
  const integrated = values?.integratedWithOtherSystems;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create');
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const persistEntries = async (nextEntries) => {
    const persisted = await persistWizardNestedCollection(
      softwareId,
      'integrations',
      nextEntries
    );
    onSetField?.('entries', persisted);
    return persisted;
  };

  const persistFlag = async (nextIntegrated) => {
    onSetField?.('integratedWithOtherSystems', nextIntegrated);
    if (!softwareId) {
      toast.error('Save system identification first so this record can be linked to the software.');
      return;
    }
    setSaving(true);
    try {
      await persistWizardSoftwareFields(softwareId, {
        integratedWithOtherSystems: nextIntegrated,
      });
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to save integration choice.'));
    } finally {
      setSaving(false);
    }
  };

  const openCreate = () => {
    if (!softwareId) {
      toast.error('Save system identification first so integrations can be linked to this software.');
      return;
    }
    setDialogMode('create');
    setEditing(emptyIntegrationRow());
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setDialogMode('edit');
    setEditing({ ...row });
    setDialogOpen(true);
  };

  const handleIntegratedChange = async (event) => {
    const nextIntegrated = event.target.value === 'yes';
    await persistFlag(nextIntegrated);
    if (nextIntegrated) {
      if (!softwareId) return;
      setDialogMode('create');
      setEditing(emptyIntegrationRow());
      setDialogOpen(true);
    } else {
      setDialogOpen(false);
      setEditing(null);
    }
  };

  const removeRow = async (key) => {
    if (!softwareId) {
      onSetField?.('entries', entries.filter((e) => e.key !== key));
      return;
    }
    setSaving(true);
    try {
      await persistEntries(entries.filter((e) => e.key !== key));
      toast.success('Integration removed.');
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to remove integration.'));
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async (row) => {
    const next =
      dialogMode === 'edit'
        ? entries.map((e) => (e.key === row.key ? row : e))
        : [...entries, row];

    setSaving(true);
    try {
      if (!values?.integratedWithOtherSystems) {
        onSetField?.('integratedWithOtherSystems', true);
      }
      await persistEntries(next);
      toast.success(
        dialogMode === 'edit' ? 'Integration updated.' : 'Integration saved and linked to this software.'
      );
      setDialogOpen(false);
      setEditing(null);
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to save integration.'));
      throw error;
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <FormControl component="fieldset" sx={{ mb: 2 }}>
        <FormLabel component="legend" sx={{ fontWeight: 700, mb: 0.75 }}>
          Is this system integrated with any other system?
        </FormLabel>
        <RadioGroup
          row
          value={integrated === true ? 'yes' : integrated === false ? 'no' : ''}
          onChange={handleIntegratedChange}
        >
          <FormControlLabel value="yes" control={<Radio size="small" />} label="Yes" disabled={saving} />
          <FormControlLabel value="no" control={<Radio size="small" />} label="No" disabled={saving} />
        </RadioGroup>
      </FormControl>

      {integrated === true ? (
        <>
          <Box display="flex" justifyContent="flex-end" alignItems="center" mb={1.5} gap={1} flexWrap="wrap">
            <Button
              variant="contained"
              color="secondary"
              startIcon={<AddIcon />}
              onClick={openCreate}
              disabled={saving}
              sx={{ textTransform: 'none' }}
            >
              Add integration
            </Button>
          </Box>

          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              border: `1px solid ${alpha(primary, isDark ? 0.35 : 0.14)}`,
              borderRadius: 2,
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Source</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Target</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Direction</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography variant="body2" color="text.secondary" sx={{ py: 1.5 }}>
                        No integrations yet. Add one for each connected system.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  entries.map((row) => (
                    <TableRow key={row.key}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {row.sourceSystem || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>{row.targetSystem || '—'}</TableCell>
                      <TableCell>{formatEnumLabel(row.integrationType) || '—'}</TableCell>
                      <TableCell>{formatEnumLabel(row.dataFlowDirection) || '—'}</TableCell>
                      <TableCell>
                        {row.integrationStatus ? (
                          <Chip size="small" label={formatEnumLabel(row.integrationStatus)} />
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton size="small" color="primary" onClick={() => openEdit(row)} disabled={saving}>
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remove">
                          <IconButton size="small" color="error" onClick={() => removeRow(row.key)} disabled={saving}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : integrated === false ? (
        <Typography variant="body2" color="text.secondary">
          This system is not integrated with other systems.
        </Typography>
      ) : null}

      {integrated === true ? (
        <IntegrationDialog
          open={dialogOpen}
          mode={dialogMode}
          value={editing}
          saving={saving}
          currentSystemName={softwareName}
          onClose={() => {
            if (saving) return;
            setDialogOpen(false);
            setEditing(null);
          }}
          onSave={handleSave}
        />
      ) : null}
    </Box>
  );
}
